import { Account, OrgProgram, Organization, Payer, SortOrder } from '@app/core/models';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { sortObjects } from '../../utils/functional';
import { ASC, DESC } from '../../utils/sorting';
import { createOrEditOrganizationSuccess, deleteOrganization, deleteOrganizationFailure, deleteOrganizationSuccess, deletePrograms, deleteProgramsFailure, deleteProgramsSuccess, getOrganization, loadOrderAccountsFailure, loadOrderAccountsSuccess, loadOrgOrderAccountsFailure, loadOrgOrderAccountsSuccess, loadOrgPayerAccounts, loadOrgPayerAccountsSuccess, loadOrganizationsFailure, loadOrganizationsSuccess, loadPayerAccountsSuccess, loadPrograms, loadProgramsFailure, loadProgramsSuccess, setOrderAccountsSortOrder, setProgramsSortOrder, setSortOrder } from '../actions/organization.actions';

export interface OrganizationsState {
  organization?: Organization;
  organizations?: Organization[];
  organizationConfirmedForDeletion?: Organization;
  organizationToDelete?: Organization;
  payerAccounts?: Payer[];
  orderAccounts?: Account[];
  sortOrder?: SortOrder;
  orderAccountsSortOrder?: SortOrder;
  orgAccounts?: Account[];
  programs?: OrgProgram[];
  programsSortOrder?: SortOrder;
  error?: {
    type: string,
    msg: string
  }
}

const initialState: OrganizationsState = {
  organization: null,
  organizations: null,
  organizationConfirmedForDeletion: null,
  organizationToDelete: null,
  payerAccounts: null,
  orderAccounts: null,
  orgAccounts: null,
  sortOrder: {
    key: 'orgName',
    direction: ASC
  },
  orderAccountsSortOrder: {
    key: 'accountNumber',
    direction: ASC,
  },
  programs: null,
  programsSortOrder: {
    key: 'id',
    direction: ASC,
  },
  error: null
};

export const organizationsReducer = createReducer(
  initialState,
  on(getOrganization, (state, action) => {
    const organization = (state.organizations && state.organizations.find(org => org.orgId === action.payload)) || null;

    return Object.assign({}, state, {
      organization: organization
    } as OrganizationsState);
  }),
  on(loadOrganizationsSuccess, (state, action) => ({ ...state, organizations: sortObjects(action.payload, state.sortOrder) })),
  on(loadOrganizationsFailure, (state, action) => ({ ...state, error: { type: 'loadOrganizations', msg: action.payload } })),
  on(createOrEditOrganizationSuccess, (state) => ({ ...state })),
  
  on(loadPrograms, (state) => ({ ...state })),
  on(loadProgramsSuccess, (state, action) => ({ ...state, programs: action.programs })),
  on(loadProgramsFailure, (state) => ({ ...state })),

  on(deletePrograms, (state) => ({ ...state })),
  on(deleteProgramsSuccess, (state) => ({ ...state })),
  on(deleteProgramsFailure, (state) => ({ ...state })),

  on(deleteOrganization, (state, action) => ({ ...state, organization: action.payload })),
  on(deleteOrganizationSuccess, (state, action) => ({ ...state, orgName: action.orgName })),
  on(deleteOrganizationFailure, (state, action) => ({ ...state, orgName: action.orgName })),

  on(loadPayerAccountsSuccess, (state, action) => ({ ...state, payerAccounts: action.payload })),

  on(loadOrderAccountsSuccess, (state, action) => ({ ...state, orderAccounts: action.payload })),
  on(loadOrderAccountsFailure, (state, action) => ({ ...state, error: { type: 'loadOrderAccounts', msg: action.payload } })),

  on(loadOrgPayerAccounts, (state) => ({ ...state })),

  on(loadOrgPayerAccountsSuccess, (state, action) => ({ ...state, orgAccounts: sortObjects(action.payload, state.sortOrder) })),

  on(loadOrgOrderAccountsSuccess, (state, action) => ({ ...state, orderAccounts: sortObjects(action.payload, state.orderAccountsSortOrder) })),

  on(loadOrgOrderAccountsFailure, (state, action) => ({ ...state, error: { type: 'loadOrgOrderAccounts', msg: action.payload } })),

  on(setSortOrder, (state, action) => {
    const newSortOrder = {
      key: action.payload,
      direction: state.sortOrder.direction
    } as SortOrder;

    if (newSortOrder.key === state.sortOrder.key) {
      newSortOrder.direction = state.sortOrder.direction === ASC ? DESC : ASC;
    }
    else {
      // different key, sort by key and initial state direction
      newSortOrder.direction = initialState.sortOrder.direction;
    }

    const organizations = sortObjects(state.organizations, newSortOrder);
    const orgAccounts = sortObjects(state.orgAccounts, newSortOrder);

    return Object.assign({}, state, {
      organizations: organizations,
      orgAccounts: orgAccounts,
      sortOrder: newSortOrder
    } as OrganizationsState);
  }),
  on(setOrderAccountsSortOrder, (state, action) => {
    const newSortOrder = {
      key: action.payload,
      direction: state.orderAccountsSortOrder.direction
    } as SortOrder;


    if (newSortOrder.key === state.orderAccountsSortOrder.key) {
      // same key, toggle sort direction
      newSortOrder.direction = state.orderAccountsSortOrder.direction === ASC ? DESC : ASC;
    }
    else {
      // different key, sort by key and initial state direction
      newSortOrder.direction = initialState.orderAccountsSortOrder.direction;
    }

    const orderAccounts = sortObjects(state.orderAccounts, newSortOrder);

    return Object.assign({}, state, {
      orderAccounts: orderAccounts,
      orderAccountsSortOrder: newSortOrder
    } as OrganizationsState);
  }),
  on(setProgramsSortOrder, (state, action) => {
    const newSortOrder = {
      key: action.payload,
      direction: state.programsSortOrder.direction
    } as SortOrder;


    if (newSortOrder.key === state.programsSortOrder.key) {
      // same key, toggle sort direction
      newSortOrder.direction = state.programsSortOrder.direction === ASC ? DESC : ASC;
    }
    else {
      // different key, sort by key and initial state direction
      newSortOrder.direction = initialState.programsSortOrder.direction;
    }

    const programs = sortObjects(state.programs, newSortOrder);

    return Object.assign({}, state, {
      programs: programs,
      programsSortOrder: newSortOrder
    } as OrganizationsState);
  })
);

export const selectOrganizationsState = createFeatureSelector<OrganizationsState>('organizationsState');
export const selectCurrentOrganizations = createSelector(selectOrganizationsState, (state) => state.organizations);
export const selectCurrentOrganization = createSelector(selectOrganizationsState, (state) => state.organization);
